import { useRef } from 'react'
import { RECONNECT_ENTRY_POINTS, type ProviderType, type ReconnectEntryPoint } from './types'

/**
 * The Plaid OAuth handoff — #414.
 *
 * Plaid's Link UI navigates the browser FULLY AWAY from this app for an
 * OAuth institution (the user picks their bank, Plaid sends them to it, and
 * it sends them back) rather than staying inside the iframe Link normally
 * opens. That destroys every in-memory value the running SPA had, including
 * whatever `useAggregator.ts`'s `useLinkFlow`/`useReauthFlow` were awaiting
 * — so resuming after the round trip needs its own persisted state and its
 * own return route, both of which live here.
 *
 * One module owns `sessionStorage`'s key. `plaid.ts` is the only writer
 * (`savePendingPlaidOAuth`, before `open()`, and `clearPendingPlaidOAuth` on
 * every exit that does NOT navigate away), and `PlaidOAuthReturn.tsx` is the
 * only reader (`usePendingPlaidOAuth`) — nothing else touches the key.
 */

/**
 * `/plaid` — where Plaid's dashboard has a redirect URI registered, and
 * where an OAuth institution actually returns the browser.
 *
 * No trailing slash, matching every other route constant in `App.tsx`
 * (`ACCOUNT_CONNECT_PATH` etc.) — verified this still matches the
 * registered `https://app-dev.tracke5.com/plaid/` (WITH the slash) against
 * `react-router@8.3.0`'s own `compilePath`
 * (`node_modules/react-router/dist/development/lib/router/utils.js:510`):
 * with the default `end: true`, the compiled matcher always appends
 * `\/*$`, so a route declared as `/plaid` matches `/plaid`, `/plaid/` and
 * `/plaid//` identically. Not assumed — read the regex construction.
 *
 * `App.tsx` declares its OWN copy of this literal (`PLAID_OAUTH_RETURN_PATH`)
 * rather than importing it from here: `App.test.tsx`'s route-table guard
 * resolves a `path={CONSTANT}` against a literal `const NAME = '...'`
 * declared IN `App.tsx`'s own source text, so an imported constant would be
 * invisible to it. `plaidOAuth.test.ts` cross-checks the two literals stay
 * in sync instead.
 */
const REDIRECT_URI_PATH = '/plaid/'

/**
 * The one origin Plaid's dashboard has a redirect URI registered for today
 * (#414's backend half, `app-dev.tracke5.com`). Plaid requires an EXACT
 * string match including the trailing slash, so this is a short allow-list
 * rather than a guess built from `import.meta.env.MODE` or similar — an
 * origin not in this list gets `redirect_uri: null`, which is the NORMAL
 * case for production (its allow-list is deliberately empty — the prod
 * webapp and iOS share `app.tracke5.com` and its default already covers
 * both), staging (nobody has registered one there), and a bare `npm run
 * dev` on `localhost` (same reason).
 *
 * **Not a `VITE_` env var.** `env.test.ts` requires the three
 * `.env.<mode>` files to carry an IDENTICAL, non-empty key set — a URI
 * that is only valid from exactly one of the three origins cannot satisfy
 * that without either a lie (a value in `.env.production` that 400s if it
 * is ever actually sent) or a hole (an empty string, which the same test
 * also forbids). Comparing the REAL, observed `window.location.origin`
 * against a short list sidesteps both, and growing this list the day
 * staging or production get a registered URI of their own is one string,
 * not new env plumbing.
 */
const KNOWN_OAUTH_ORIGINS = ['https://app-dev.tracke5.com']

/**
 * `null` in every environment but the one Plaid has a redirect URI
 * registered for — read at call time from the real origin, never cached,
 * so `useAggregator.ts` sends a fresh, correct answer on every link/reauth
 * session it mints (and a test can swap `window.location` between
 * assertions).
 */
export function plaidOAuthRedirectUri(): string | null {
  const origin = window.location.origin
  return KNOWN_OAUTH_ORIGINS.includes(origin) ? `${origin}${REDIRECT_URI_PATH}` : null
}

/**
 * What survives the full-page navigation an OAuth institution causes.
 *
 * `kind`/`itemId` are needed on return to issue the RIGHT completion
 * request — `useAggregator.ts`'s `useLinkFlow`/`useReauthFlow` already
 * diverge here (a fresh link POSTs `/link-session/complete`; a Plaid reauth
 * skips straight to `/reauth/complete`, since Link's update mode IS the
 * whole reconnect) and reloading the page in the middle does not erase that
 * divergence.
 */
/**
 * `provider` rides along as DATA — `session.provider`, the wire response's
 * own field — rather than a literal `'plaid'` written here. `registry.test
 * .ts`'s "no provider name string literal appears outside registry.ts" is a
 * real architectural guard (#397), and `usePlaidOAuthResume` needs a real
 * `ProviderType` to dispatch back through `getAdapter` on return; reading it
 * off the persisted session keeps this file (and `useAggregator.ts`) clean
 * of the literal that guard polices, even though only Plaid's
 * `redirectFlow.prepare` writes this today.
 *
 * `stateNonce` — architect review (#414, B1). `LinkSessionResponse.state_nonce`
 * is declared on the provider-agnostic response with no provider condition
 * (`openapi.json`, checked directly), and `completeLinkSession` already
 * forwards whatever `useLinkFlow`/`useReauthFlow` read off `session.stateNonce`
 * for every OTHER entrance to this same flow. Dropping it here — the one
 * entrance nobody can exercise end to end — would be a silent divergence: if
 * Plaid ever starts minting one, the ordinary link path keeps working and only
 * the OAuth return breaks, in production. Carried through unconditionally
 * instead, `null` today because that is what Plaid sessions carry today, not
 * because this type assumes it always will.
 */
export type PendingPlaidOAuth =
  | { kind: 'link'; linkToken: string; provider: ProviderType; stateNonce: string | null }
  | { kind: 'reauth'; linkToken: string; provider: ProviderType; itemId: string; stateNonce: string | null }
  /**
   * A RECONNECT of a disconnected bank. **`stateNonce` is a non-null string
   * here, never `null`**: the backend binds the nonce to `relinkItemId` and
   * refuses a relink completion without it, so a stored relink without one
   * could only ever complete wrongly, and `isPendingPlaidOAuth` rejects it.
   * The nonce is never read from the return URL — Plaid's return carries
   * `oauth_state_id` only — so the value sent on completion is this stored
   * one, which the server validates (single use, this user, this
   * `relinkItemId`). Held in `sessionStorage` (this tab, this session), the
   * same place and lifetime the link flow's nonce already has.
   */
  | {
      kind: 'relink'
      linkToken: string
      provider: ProviderType
      relinkItemId: string
      stateNonce: string
      /** Where the reconnect started — for its analytics outcome after the handoff.
       *  Absent when the stored value was not one of the allowed members. */
      entryPoint?: ReconnectEntryPoint
      /** `Date.now()` at `start()` (before the session call), so the OAuth return's
       *  duration spans the same interval as the in-page path's. Not `savedAt`, which
       *  is stamped later, at `prepare()`, and exists only for the expiry check. */
      startedAt?: number
    }

/** What the one read of the stored session found. `expired_relink` is a real
 *  reconnect that outlived its nonce; `none` is everything else unusable. */
export type PlaidOAuthSession =
  | { status: 'pending'; pending: PendingPlaidOAuth }
  | { status: 'expired_relink'; entryPoint?: ReconnectEntryPoint }
  | { status: 'none' }

const NO_SESSION: PlaidOAuthSession = { status: 'none' }

const STORAGE_KEY = 'tracke5.plaid_oauth_pending'

/**
 * A stored RECONNECT older than this is not resumed. It is the backend's nonce
 * TTL (10 minutes): past it the server refuses the completion anyway, so
 * resuming would spend the user's return trip on a certain 400. Only a relink
 * carries `savedAt` — it is the only kind whose nonce expires.
 */
export const RELINK_PENDING_MAX_AGE_MS = 10 * 60 * 1000

/**
 * Loose on purpose about `stateNonce`'s shape — it is normalised separately,
 * below, so a value written by an OLDER tab (before this field existed) is
 * accepted and defaulted rather than rejected outright as malformed.
 */
type StoredShape =
  | { kind: 'link'; linkToken: string; provider: string }
  | { kind: 'reauth'; linkToken: string; provider: string; itemId: string }
  | {
      kind: 'relink'
      linkToken: string
      provider: string
      relinkItemId: string
      stateNonce: string
      savedAt: number
      entryPoint?: unknown
      startedAt?: unknown
    }

function isPendingPlaidOAuth(value: unknown): value is StoredShape {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  if (typeof v.linkToken !== 'string' || v.linkToken === '') return false
  if (typeof v.provider !== 'string' || v.provider === '') return false
  if (v.kind === 'link') return true
  if (v.kind === 'reauth') return typeof v.itemId === 'string' && v.itemId !== ''
  // A relink with no id to relink, or no nonce to prove the session, is
  // rejected outright — never defaulted, never completed as an ordinary link.
  if (v.kind === 'relink') return isWellFormedRelink(v) && !isPastNonceLifetime(v)
  return false
}

function isWellFormedRelink(v: Record<string, unknown>): boolean {
  return (
    typeof v.relinkItemId === 'string' &&
    v.relinkItemId !== '' &&
    typeof v.stateNonce === 'string' &&
    v.stateNonce !== ''
  )
}

/** No `savedAt`, one past the nonce's lifetime, or one in the future (a value
 *  that could never expire) is the expired state: never resumed. */
function isPastNonceLifetime(v: Record<string, unknown>): boolean {
  const age = typeof v.savedAt === 'number' ? Date.now() - v.savedAt : Number.NaN
  return !(age >= 0 && age <= RELINK_PENDING_MAX_AGE_MS)
}

/** A relink that WAS a real reconnect (id and nonce present) but is too old to
 *  resume. Reported apart from "nothing stored" because the recovery differs:
 *  adding the bank fresh would duplicate the connection a reconnect exists to
 *  replace. */
function isExpiredRelink(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.linkToken === 'string' &&
    v.linkToken !== '' &&
    typeof v.provider === 'string' &&
    v.provider !== '' &&
    v.kind === 'relink' &&
    isWellFormedRelink(v) &&
    isPastNonceLifetime(v)
  )
}

/** Storage is untrusted: a value outside the allowed members is dropped, never thrown on
 *  (an unknown one would throw in dev and vanish in production). */
function validEntryPoint(value: unknown): ReconnectEntryPoint | undefined {
  return RECONNECT_ENTRY_POINTS.find((p) => p === value)
}

function validStartedAt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
}

/** `string` passes through; anything else (absent, `null`, a future shape
 *  change) defaults to `null` rather than rejecting the whole stored value. */
function normalizedStateNonce(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/**
 * Written right before `adapter.open()`, for EVERY Plaid session — link or
 * reauth, never conditionally on whether this particular one turns out to
 * need OAuth. Nothing on this side can tell in advance: that is decided
 * inside Plaid's own Link UI, after the user picks an institution, and by
 * the time it navigates the tab away every in-memory value is already gone.
 * Writing unconditionally, this early, is what makes that loss survivable
 * for the (rare) institutions that turn out to need it, at the cost of a
 * write that goes unread for every institution that does not.
 */
export function savePendingPlaidOAuth(pending: PendingPlaidOAuth): void {
  try {
    // `savedAt` is storage-only (a relink's expiry check reads it) and is not
    // part of what `takePendingPlaidOAuth` hands back.
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(pending.kind === 'relink' ? { ...pending, savedAt: Date.now() } : pending),
    )
  } catch {
    // Storage refused — blocked cookies, a sandboxed iframe, an enterprise
    // policy. An OAuth institution that navigates away now has nothing to
    // resume with, and the user lands on `/plaid`'s own "no session found"
    // state — a downgrade, not a crash. A non-OAuth institution never
    // leaves the page and never reads this back at all.
  }
}

/**
 * Every exit from Plaid's own Link UI that does NOT navigate the browser
 * away — success, the user closing the window, an SDK error, the
 * ready-timeout — calls this. Without it a value written for one session
 * would outlive it and be read back, wrongly, by whatever the NEXT visit to
 * `/plaid` happens to be.
 */
export function clearPendingPlaidOAuth(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear if the write above never landed either.
  }
}

/**
 * Read once and clear — same contract as `returnTo.ts`'s `takeReturnTo`,
 * for the identical reason: a pending session that outlived its own resume
 * would be replayed against a second, unrelated visit to `/plaid` (a
 * bookmark, a second tab).
 */
function takePendingPlaidOAuth(): PlaidOAuthSession {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    return NO_SESSION
  }
  if (raw === null) return NO_SESSION
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return NO_SESSION
  }
  if (isExpiredRelink(parsed)) {
    return { status: 'expired_relink', entryPoint: validEntryPoint((parsed as Record<string, unknown>).entryPoint) }
  }
  if (!isPendingPlaidOAuth(parsed)) return NO_SESSION
  return { status: 'pending', pending: pendingOf(parsed) }
}

function pendingOf(parsed: StoredShape): PendingPlaidOAuth {
  // Reconstructed explicitly rather than spread — `stateNonce` is normalised
  // here (B1, architect review) so a value written by an older tab, before
  // this field existed, is accepted and defaulted rather than rejected.
  const provider = parsed.provider as PendingPlaidOAuth['provider']
  if (parsed.kind === 'relink') {
    return {
      kind: 'relink',
      linkToken: parsed.linkToken,
      provider,
      relinkItemId: parsed.relinkItemId,
      stateNonce: parsed.stateNonce,
      entryPoint: validEntryPoint(parsed.entryPoint),
      startedAt: validStartedAt(parsed.startedAt),
    }
  }
  const stateNonce = normalizedStateNonce((parsed as Record<string, unknown>).stateNonce)
  return parsed.kind === 'link'
    ? { kind: 'link', linkToken: parsed.linkToken, provider, stateNonce }
    : { kind: 'reauth', linkToken: parsed.linkToken, provider, itemId: parsed.itemId, stateNonce }
}

/**
 * The one read `PlaidOAuthReturn.tsx` is allowed to do — `takePendingPlaidOAuth`
 * itself is not exported, so nothing else in this app can read this key.
 *
 * **A lazy `useRef` guard, not `useState(() => takePendingPlaidOAuth())`.**
 * `returnTo.ts`'s `useReturnTo` documents why the two are not equivalent:
 * React double-invokes a `useState` initialiser under `<StrictMode>` in
 * development too (`main.tsx` wraps the whole app in it), which would
 * silently consume-and-discard the real value on the throwaway first pass —
 * exactly the bug that cost M1-R7 its destination before this same fix was
 * applied there.
 */
export function usePlaidOAuthSession(): PlaidOAuthSession {
  const session = useRef<PlaidOAuthSession | undefined>(undefined)
  if (session.current === undefined) session.current = takePendingPlaidOAuth()
  return session.current
}

/** The pending session alone — `null` for an expired relink and for nothing at
 *  all. `PlaidOAuthReturn` reads `usePlaidOAuthSession` instead, because it has
 *  to tell those two apart. */
export function usePendingPlaidOAuth(): PendingPlaidOAuth | null {
  const session = usePlaidOAuthSession()
  return session.status === 'pending' ? session.pending : null
}
